import {
  faAddressCard,
  faAnalytics,
  faAtlas,
  faBox,
  faBoxCheck,
  faBoxUsd,
  faCamera,
  faCoins,
  faComments,
  faDatabase,
  faEdit,
  faFileAlt,
  faFileCheck,
  faGlobe,
  faHandHoldingUsd,
  faHistory,
  faHome,
  faImages,
  faList,
  faPencil,
  faReceipt,
  faTachometer,
  faWrench,
} from '@fortawesome/pro-light-svg-icons'

import { generateApplicationUrl } from '@domain-group/javascript-utils'

import useCurrentUser from '@hooks/useCurrentUser'

import type { CampaignModel } from '@models/types'

import LogoNavIcon from '@components/LogoNavIcon'

type GenerateNavigationItemsParams = {
  campaign: CampaignModel,
  campaignPermissions: {
    ad_copy?: { manage?: boolean },
    contacts?: { manage?: boolean },
    details?: { manage?: boolean },
    invoices?: { manage?: boolean },
    media_selection?: { manage?: boolean },
    media?: { manage?: boolean }, // Media has been deprecated in favour of media_selection and photos_and_plans.
    orders?: { manage?: boolean },
    photos_and_plans?: { manage?: boolean },
    portals?: { manage?: boolean },
    proposals?: { manage?: boolean },
    quotes?: { manage?: boolean },
  } | null,
  currentUserPayload: ReturnType<typeof useCurrentUser>,
}

const generateNavigationItems = (params: GenerateNavigationItemsParams) => {
  const {
    campaign: { id },
    campaignPermissions,
    currentUserPayload,
  } = params

  const {
    callbacks: {
      loginToExternalPlatform,
    },
  } = currentUserPayload

  const baseUrl = `#/campaigns/${id}/edit`

  // @ts-expect-error TS2339
  const { applicationUrl: engageUrl } = generateApplicationUrl({
    applicationKey: 'engage',
    // @ts-expect-error TS2322
    environment: process.env.REACT_APP_APPLICATION_ENV,
  })

  const {
    ad_copy = {},
    contacts = {},
    details = {},
    invoices = {},
    media = {},
    media_selection = {},
    orders = {},
    photos_and_plans = {},
    portals = {},
    proposals = {},
    quotes = {},
  } = campaignPermissions || {}

  return ({
    sections: [
      {
        key: 'dashboard',
        visible: true,
        items: [
          {
            href: `${baseUrl}/`,
            icon: faTachometer,
            key: 'overview',
            title: 'Overview',
            visible: true,
          },
          {
            href: `${baseUrl}/conversations`,
            icon: faComments,
            key: 'conversations',
            title: 'Conversations',
            visible: true,
          },
          {
            href: `${baseUrl}/contacts`,
            icon: faAddressCard,
            key: 'contacts',
            title: 'Contacts',
            visible: contacts?.manage || false,
          },
          {
            icon: faHome,
            key: 'campaigns',
            title: 'Property Details',
            visible: true,
            items: [
              {
                href: `${baseUrl}/details`,
                icon: faEdit,
                key: 'details',
                title: 'Agents & Details',
                visible: details?.manage || false,
              },
              {
                href: `${baseUrl}/comparables`,
                icon: faList,
                key: 'comparables',
                title: 'Comparables',
                visible: proposals?.manage || false,
              },
              {
                href: `${baseUrl}/data-store`,
                icon: faDatabase,
                key: 'data-store',
                title: 'Data Store',
                visible: details?.manage || false,
              },
              {
                href: `${baseUrl}/portal-data`,
                icon: faAtlas,
                key: 'portal-data',
                title: 'Portal Data',
                visible: portals?.manage || false,
              },
            ],
          },
          {
            icon: faCoins,
            key: 'finance',
            title: 'Quoting & Finance',
            visible: true,
            items: [
              {
                href: `${baseUrl}/quotes`,
                icon: faFileAlt,
                key: 'finance-quoting',
                title: 'Quoting',
                visible: quotes?.manage || false,
              },
              {
                href: `${baseUrl}/accounting`,
                icon: faReceipt,
                key: 'finance-accounting',
                title: 'Accounting',
                visible: invoices?.manage || false,
              },
              {
                href: `${baseUrl}/accounting/order-reconciliation`,
                icon: faHandHoldingUsd,
                key: 'finance-reconciliation',
                title: 'Order Reconciliation',
                visible: invoices?.manage || false,
              },
            ],
          },
          {
            icon: faBoxUsd,
            key: 'orders',
            title: 'Orders',
            visible: orders?.manage || false,
            items: [
              {
                href: `${baseUrl}/orders/pending`,
                icon: faBox,
                key: 'orders-pending',
                title: 'Pending',
                visible: true,
              },
              {
                href: `${baseUrl}/orders/approved`,
                icon: faBoxCheck,
                key: 'orders-approved',
                title: 'Approved',
                visible: true,
              },
            ],
          },
          {
            icon: faImages,
            key: 'media',
            title: 'Media & Copy',
            visible: photos_and_plans?.manage
              || media_selection?.manage
              || ad_copy?.manage
              || media?.manage || false,
            items: [
              {
                href: `${baseUrl}/media`,
                icon: faCamera,
                key: 'media-photos',
                title: 'Photos & Plans',
                visible: photos_and_plans?.manage || media?.manage || false,
              },
              {
                href: `${baseUrl}/media-selection`,
                icon: faFileCheck,
                key: 'media-selection',
                title: 'Media Selection',
                visible: media_selection?.manage || media?.manage || false,
              },
              {
                href: `${baseUrl}/adcopy`,
                icon: faPencil,
                key: 'media-adcopy',
                title: 'Ad Copy',
                visible: ad_copy?.manage || false,
              },
            ],
          },
          {
            icon: faWrench,
            key: 'tools',
            title: 'Tools',
            visible: true,
            items: [
              {
                href: `${baseUrl}/portals`,
                icon: faGlobe,
                key: 'tools-portals',
                title: 'Portals',
                visible: portals?.manage || false,
              },
              {
                href: `${baseUrl}/tracking-links`,
                icon: faAnalytics,
                key: 'tools-tracking',
                title: 'Tracking Links',
                visible: true,
              },
              {
                href: `${baseUrl}/activity`,
                icon: faHistory,
                key: 'tools-activity',
                title: 'Activity',
                visible: true,
              },
            ],
          },
        ],
      },
      {
        key: 'external',
        title: 'External',
        visible: true,
        items: [
          {
            iconComponent: LogoNavIcon('engage', 'rgb(140, 140, 142, 0.5)'),
            key: 'engage',
            onClick: () => loginToExternalPlatform({
              application_key: 'engage',
              hash: `/?integration_platform_key=realhub&external_id=${id}`,
              path: engageUrl,
            }),
            title: 'Engage',
            visible: true,
          },
        ],
      },
    ],
  })
}

export default generateNavigationItems
